<template>
  <Teleport to="body">
    <Transition name="bottom-sheet" @after-enter="focusInitial">
      <div v-if="modelValue" class="bottom-sheet" @keydown.esc="close">
        <div class="bottom-sheet__backdrop" @click="onBackdrop"></div>
        <section
          ref="panel"
          class="bottom-sheet__panel"
          role="dialog"
          aria-modal="true"
          :aria-label="title || undefined"
          tabindex="-1"
        >
          <span class="bottom-sheet__grabber" aria-hidden="true"></span>
          <header v-if="title || !persistent" class="bottom-sheet__header">
            <h2 class="bottom-sheet__title">{{ title }}</h2>
            <button v-if="!persistent" type="button" class="bottom-sheet__close" aria-label="Закрыть" @click="close">
              <AppIcon name="close" :size="20" />
            </button>
          </header>
          <div class="bottom-sheet__body cb-scroll">
            <slot />
          </div>
          <footer v-if="$slots.footer" class="bottom-sheet__footer">
            <slot name="footer" />
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
/**
 * BottomSheet — нижняя шторка (модальная), телепортируется в <body>.
 *
 * <BottomSheet v-model="renameOpen" title="Имя в игре">
 *   <input class="cb-input" v-model="name" autofocus />
 *   <template #footer><AppButton block @click="save">Сохранить</AppButton></template>
 * </BottomSheet>
 *
 * props: modelValue (открыта), title, persistent (нельзя закрыть тапом по фону/Esc/крестиком).
 * emits: update:modelValue, close.
 * После открытия фокус уходит на элемент с [autofocus] внутри, иначе на саму панель.
 */
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import AppIcon from "./AppIcon.vue";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "" },
  persistent: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "close"]);
const panel = ref(null);

function close() {
  if (props.persistent) return;
  emit("update:modelValue", false);
  emit("close");
}

function onBackdrop() {
  close();
}

function focusInitial() {
  nextTick(() => {
    if (!panel.value) return;
    const target = panel.value.querySelector("[autofocus]");
    (target || panel.value).focus({ preventScroll: true });
  });
}

// Блокируем прокрутку страницы под открытой шторкой.
let previousOverflow = "";
watch(
  () => props.modelValue,
  (open) => {
    if (typeof document === "undefined") return;
    if (open) {
      previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = previousOverflow;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  if (props.modelValue && typeof document !== "undefined") document.body.style.overflow = previousOverflow;
});
</script>

<style scoped>
.bottom-sheet {
  position: fixed;
  inset: 0;
  z-index: var(--cb-z-sheet);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.bottom-sheet__backdrop {
  position: absolute;
  inset: 0;
  background: var(--cb-overlay);
  -webkit-backdrop-filter: blur(4px);
  backdrop-filter: blur(4px);
}

.bottom-sheet__panel {
  position: relative;
  width: 100%;
  max-width: var(--cb-app-max);
  max-height: 85vh;
  max-height: 85dvh;
  display: flex;
  flex-direction: column;
  padding: 10px calc(var(--cb-gutter) + var(--cb-safe-right)) calc(16px + var(--cb-safe-bottom)) calc(var(--cb-gutter) + var(--cb-safe-left));
  border-radius: var(--cb-radius-xl) var(--cb-radius-xl) 0 0;
  background: linear-gradient(180deg, var(--cb-surface-2) 0%, var(--cb-surface) 100%);
  border: 1px solid var(--cb-border-strong);
  border-bottom: 0;
  box-shadow: var(--cb-shadow-pop), var(--cb-inner-highlight);
  outline: none;
}

.bottom-sheet__grabber {
  align-self: center;
  width: 44px;
  height: 5px;
  margin-bottom: 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
}

.bottom-sheet__header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.bottom-sheet__title {
  flex: 1 1 auto;
  font-size: 20px;
  font-weight: 700;
}

.bottom-sheet__close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 50%;
  border: 1px solid var(--cb-border-strong);
  background: transparent;
  color: var(--cb-text-2);
  cursor: pointer;
}

.bottom-sheet__body {
  flex: 1 1 auto;
  min-height: 0;
}

.bottom-sheet__footer {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 16px;
}

.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity var(--cb-dur) var(--cb-ease);
}

.bottom-sheet-enter-active .bottom-sheet__panel,
.bottom-sheet-leave-active .bottom-sheet__panel {
  transition: transform var(--cb-dur) var(--cb-ease);
}

.bottom-sheet-enter-from,
.bottom-sheet-leave-to {
  opacity: 0;
}

.bottom-sheet-enter-from .bottom-sheet__panel,
.bottom-sheet-leave-to .bottom-sheet__panel {
  transform: translateY(100%);
}
</style>
